import { useEffect, useRef, useState } from 'react';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';

type DocxPreviewRendererProps = {
  blob: Blob | null;
  className?: string;
  error?: string | null;
  onRetry?: () => void;
  status: 'idle' | 'generating' | 'ready' | 'error';
};

export function DocxPreviewRenderer({
  blob,
  className,
  error,
  onRetry,
  status,
}: DocxPreviewRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [renderState, setRenderState] = useState<'idle' | 'rendering' | 'ready' | 'error'>('idle');
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !blob || status !== 'ready') {
      return;
    }

    let cancelled = false;
    setRenderState('rendering');
    setRenderError(null);
    container.replaceChildren();

    (async () => {
      try {
        const { renderAsync } = await import('docx-preview');

        if (cancelled) {
          return;
        }

        await renderAsync(blob, container, undefined, {
          breakPages: true,
          experimental: true,
          ignoreHeight: false,
          ignoreLastRenderedPageBreak: true,
          ignoreWidth: false,
          inWrapper: true,
          renderEndnotes: true,
          renderFooters: true,
          renderFootnotes: true,
          renderHeaders: true,
          trimXmlDeclaration: true,
          useBase64URL: false,
        });

        if (!cancelled) {
          setRenderState('ready');
        }
      } catch (renderException) {
        if (!cancelled) {
          setRenderState('error');
          setRenderError(
            renderException instanceof Error
              ? renderException.message
              : 'Aperçu Word indisponible.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob, status]);

  const isLoading = status === 'generating' || (status === 'ready' && renderState === 'rendering');
  const failed = status === 'error' || renderState === 'error';
  const errorMessage = error ?? renderError;

  return (
    <div className={cn('relative h-full w-full', className)}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80 text-sm text-muted-foreground backdrop-blur-sm">
          <Loader2Icon className="size-5 animate-spin" />
          <span>Préparation de l’aperçu Word…</span>
        </div>
      ) : null}

      {failed ? (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/90 px-6 text-center text-sm text-muted-foreground">
          <AlertCircleIcon className="size-6 text-destructive" />
          <p>{errorMessage ?? 'Impossible d’afficher l’aperçu Word.'}</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border/60 bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Réessayer
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        ref={containerRef}
        className="docx-preview-wrapper h-full w-full overflow-auto"
        aria-busy={isLoading}
        aria-live="polite"
      />
    </div>
  );
}
